
import React, { useState } from 'react';
import { X, DollarSign, Calendar, Hash, Save, ArrowUpRight, ArrowDownLeft } from 'lucide-react';
import { Company } from './types';

interface NewTransactionModalProps {
  companies: Company[];
  onClose: () => void;
  onSubmit: (transaction: any) => void;
}

export const NewTransactionModal = ({ companies, onClose, onSubmit }: NewTransactionModalProps) => {
  const [formData, setFormData] = useState({
    description: '',
    amount: 0,
    type: 'INCOME' as 'INCOME' | 'EXPENSE',
    category: 'SaaS Subscription',
    dueDate: new Date().toISOString().split('T')[0],
    companyId: '',
    status: 'PENDING'
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.description || formData.amount <= 0) return;
    onSubmit({ ...formData, id: `tr-${Date.now()}` });
  };

  return (
    <div className="fixed inset-0 bg-[#0f172a]/95 backdrop-blur-sm z-[150] flex items-center justify-center p-6">
      <div className="bg-[#1e293b] text-white rounded-[40px] w-full max-w-lg shadow-2xl border border-white/10 overflow-hidden animate-in zoom-in-95">
        <div className="p-8 bg-[#0f172a] border-b border-white/5 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-black uppercase tracking-tight">Novo Lançamento</h2>
            <p className="text-[10px] font-bold text-primary uppercase tracking-widest">Fluxo de Caixa Master</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/5 rounded-xl text-white/50 hover:text-white transition-all">
            <X size={28} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-6">
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-white/40 uppercase tracking-widest">Descrição do Título</label>
              <input 
                required 
                autoFocus
                className="w-full bg-[#0f172a] border border-white/10 p-4 rounded-2xl outline-none font-bold text-white focus:ring-2 focus:ring-primary placeholder:text-white/20" 
                placeholder="Ex: Assinatura Mensal - Empresa X"
                value={formData.description}
                onChange={e => setFormData({...formData, description: e.target.value})}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-white/40 uppercase tracking-widest">Valor Bruto (R$)</label>
                <div className="relative">
                  <DollarSign size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-primary" />
                  <input 
                    required 
                    type="number" 
                    step="0.01"
                    className="w-full bg-[#0f172a] border border-white/10 p-4 pl-10 rounded-2xl outline-none font-bold text-white focus:ring-2 focus:ring-primary" 
                    value={formData.amount}
                    onChange={e => setFormData({...formData, amount: parseFloat(e.target.value)})}
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-white/40 uppercase tracking-widest">Data de Vencimento</label>
                <input 
                  required 
                  type="date"
                  className="w-full bg-[#0f172a] border border-white/10 p-4 rounded-2xl outline-none font-bold text-white focus:ring-2 focus:ring-primary color-scheme-dark" 
                  value={formData.dueDate}
                  onChange={e => setFormData({...formData, dueDate: e.target.value})}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-white/40 uppercase tracking-widest">Natureza</label>
                <select 
                  className="w-full bg-[#0f172a] border border-white/10 p-4 rounded-2xl outline-none font-bold text-white cursor-pointer focus:ring-2 focus:ring-primary appearance-none"
                  value={formData.type}
                  onChange={e => setFormData({...formData, type: e.target.value as any})}
                >
                  <option value="INCOME" className="bg-[#1e293b] text-white">Receber (+)</option>
                  <option value="EXPENSE" className="bg-[#1e293b] text-white">Pagar (-)</option>
                </select>
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-white/40 uppercase tracking-widest">Vincular Locadora</label>
                <select 
                  className="w-full bg-[#0f172a] border border-white/10 p-4 rounded-2xl outline-none font-bold text-white cursor-pointer focus:ring-2 focus:ring-primary appearance-none"
                  value={formData.companyId}
                  onChange={e => setFormData({...formData, companyId: e.target.value})}
                >
                  <option value="" className="bg-[#1e293b] text-white">Lançamento Global</option>
                  {companies.map(c => <option key={c.id} value={c.id} className="bg-[#1e293b] text-white">{c.name}</option>)}
                </select>
              </div>
            </div>
          </div>

          <div className="pt-4 flex gap-4">
            <button type="button" onClick={onClose} className="flex-1 py-5 bg-white/5 hover:bg-white/10 rounded-3xl font-black uppercase text-[10px] tracking-widest text-white/40">
              Cancelar
            </button>
            <button type="submit" className="flex-[2] py-5 bg-primary text-white rounded-3xl font-black uppercase text-xs tracking-widest shadow-xl shadow-primary/20 hover:brightness-110 active:scale-95 transition-all">
              Registrar Título
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
